// 공용 마이크로 인터랙션 유틸: ripple, magnetic, tilt, spotlight, fade-up reveal, count-up

export function attachRipple(el: HTMLElement) {
  const onDown = (e: PointerEvent) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = el.getBoundingClientRect();
    const span = document.createElement('span');
    span.className = 'ripple';
    const size = Math.max(rect.width, rect.height);
    span.style.width = span.style.height = `${size}px`;
    span.style.left = `${e.clientX - rect.left - size / 2}px`;
    span.style.top = `${e.clientY - rect.top - size / 2}px`;
    el.appendChild(span);
    setTimeout(() => span.remove(), 600);
  };
  el.addEventListener('pointerdown', onDown);
  return () => el.removeEventListener('pointerdown', onDown);
}

export function attachMagnetic(el: HTMLElement, strength = 12) {
  if (window.matchMedia('(pointer: coarse)').matches) return () => {};
  const onMove = (e: PointerEvent) => {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
  };
  const reset = () => (el.style.transform = '');
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerleave', reset);
  return () => {
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerleave', reset);
  };
}

export function attachTilt(el: HTMLElement, max = 8) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};
  const onMove = (e: PointerEvent) => {
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg)`;
  };
  const reset = () => (el.style.transform = 'perspective(900px) rotateX(0) rotateY(0)');
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerleave', reset);
  return () => {
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerleave', reset);
  };
}

export function attachSpotlight(el: HTMLElement) {
  const onMove = (e: PointerEvent) => {
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };
  el.addEventListener('pointermove', onMove);
  return () => el.removeEventListener('pointermove', onMove);
}

export function observeReveal(root: ParentNode = document) {
  const els = root.querySelectorAll<HTMLElement>('.reveal');
  if (!('IntersectionObserver' in window)) {
    els.forEach((e) => e.classList.add('is-visible'));
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const ent of entries) {
        if (ent.isIntersecting) {
          ent.target.classList.add('is-visible');
          io.unobserve(ent.target);
        }
      }
    },
    { threshold: 0.12 },
  );
  els.forEach((e) => io.observe(e));
  return () => io.disconnect();
}

export function countUp(el: HTMLElement, to: number, durationMs = 1200) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.textContent = String(to);
    return;
  }
  const start = performance.now();
  const from = 0;
  const ease = (t: number) => 1 - Math.pow(1 - t, 3);
  function frame(now: number) {
    const t = Math.min(1, (now - start) / durationMs);
    const v = Math.round(from + (to - from) * ease(t));
    el.textContent = v.toLocaleString('ko-KR');
    if (t < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
